import Api from 'entities/helpers/api';
import EverTrue from 'app';
import ClickToInput from 'components/controls/click-to-input';
import FolderActions from '../folder-actions';
import ListIndexTable from 'apps/lists/components/index/list-index-table';
import CommonDeleteModal from '../modal/common-delete-modal';
import { useLists } from 'apps/lists/hooks/use-lists';
import { PropTypes } from 'base/react-libs';
import { useIdentity } from 'base/identity-resolver';
import { useOpenClose, Icon, PageHeader, Button } from '@evertrue/et-components';

const FolderListDetail = ({
  id,
  loading,
  folderName,
  folderType,
  folderOwner,
  folderLists,
  total,
  search,
  handleSearchChange,
  sortModel,
  handleSortChange,
  paginationModel,
  setPaginationModel,
  updateFolder,
  removeListFromFolder,
  deleteFolder,
  updateFolderLists,
}) => {
  const isDynamic = folderType === 'DYNAMIC';
  const [isDeleteFolderModalOpen, openDeleteFolderModal, closeDeleteFolderModal] = useOpenClose();
  const [isRemoveListsModalOpen, openRemoveListsModal, closeRemoveListsModal] = useOpenClose();
  const [isBulkListsDeleteModalOpen, openBulkListsDeleteModal, closeBulkListsDeleteModal] = useOpenClose();

  const ownedOnly = false;
  const { deleteList, rowSelectionModel, updateRowSelectionModel, clearSelections, enableAllActions } = useLists(
    null,
    ownedOnly,
    folderLists
  );

  const { user } = useIdentity();
  const userIsOwner = user.id == folderOwner;

  // Folder And List Handlers
  const handleSaveFolderName = (newName) => {
    if (newName && newName !== folderName) {
      updateFolder(newName);
    }
  };

  const handleBulkListDelete = () => {
    const ids = Array.from(rowSelectionModel.ids);
    Promise.all(ids.map((listId) => Api.CONTACTS.LIST.delete({ urlArgs: { list_id: listId } })))
      .then(() => {
        updateFolderLists(ids);
        EverTrue.Alert.success('Selected Lists deleted');
      })
      .catch(() => {
        EverTrue.Alert.error('Error while deleting selected lists.');
      });
    clearSelections();
    closeBulkListsDeleteModal();
  };

  const confirmDeleteFolder = () => {
    deleteFolder(id);
    closeDeleteFolderModal();
  };

  const handleRemoveLists = () => {
    const ids = Array.from(rowSelectionModel.ids);
    Promise.all(
      ids.map((listId) =>
        Api.CONTACTS.FOLDER_LISTS.delete({ params: { folder_id: id, list_id: listId }, disableAlerts: true })
      )
    )
      .then(() => {
        updateFolderLists(ids);
        EverTrue.Alert.success('Selected lists removed from folder');
      })
      .catch(() => {
        EverTrue.Alert.error('An error occurred while removing lists from folder.');
      });
    clearSelections();
    closeRemoveListsModal();
  };

  const updateSavedSearchPref = (list, listId, type) => {
    const frequency = list[`${type}_frequency`];
    let data = {};
    if (frequency === 'never') {
      data[`${type}_enabled`] = false;
    } else {
      data[`${type}_enabled`] = true;
      data[`${type}_frequency`] = frequency;
    }
    return Api.EMS.PREFS_BY_KEY.put({
      urlArgs: { key: `list:${listId}:` },
      data: JSON.stringify(data),
      success_message: 'Notification preferences updated',
      error_message: 'Failed to update notification preferences',
    });
  };

  const updateRow = async (newRow, oldRow) => {
    const updates = [];
    if (newRow.email_frequency !== oldRow.email_frequency) {
      updates.push(updateSavedSearchPref(newRow, newRow.id, 'email'));
    }
    if (newRow.push_frequency !== oldRow.push_frequency) {
      updates.push(updateSavedSearchPref(newRow, newRow.id, 'push'));
    }
    await Promise.all(updates);
    return newRow;
  };

  return (
    <>
      <div className="task-header-container">
        <Button
          title="Go Back"
          type="simple"
          accessibilityAttributes={{ role: 'link', tabIndex: '0' }}
          onClick={() => {
            EverTrue.Navigator(isDynamic ? '/segments' : '/lists');
          }}
        >
          <Icon icon="chevron-left" size={2} />
        </Button>
        <PageHeader
          title={
            <div className="list-detail--label">
              {folderName !== 'Favorites' ? (
                <ClickToInput
                  value={folderName}
                  onSave={handleSaveFolderName}
                  showFullText={true}
                  disable={!userIsOwner}
                  maxLength={30}
                />
              ) : (
                <h1 tabIndex={-1}>{folderName}</h1>
              )}
            </div>
          }
          actions={
            userIsOwner && (
              <FolderActions
                hasSelectedItems={rowSelectionModel.ids.size > 0}
                enableAllActions={enableAllActions}
                handleDeleteFolder={openDeleteFolderModal}
                handleRemoveItems={openRemoveListsModal}
                handleItemDelete={openBulkListsDeleteModal}
                folderType={folderType}
              />
            )
          }
        />
      </div>
      <div style={{ height: 'calc(100vh - 133px)', flex: 1 }}>
        <ListIndexTable
          listType={isDynamic ? 'dynamic' : 'static'}
          tableKey="folder-lists-table"
          lists={folderLists}
          total={total}
          loading={loading}
          deleteList={async (listId) => {
            await deleteList(listId);
            updateFolderLists([listId]);
          }}
          search={search}
          onSearchChange={handleSearchChange}
          sortModel={sortModel}
          setSortModel={handleSortChange}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          rowSelectionModel={rowSelectionModel}
          updateRowSelectionModel={updateRowSelectionModel}
          onRowUpdate={updateRow}
          emptyStateIcon={isDynamic ? 'segment' : 'list'}
          emptyStateMessage={
            search
              ? `No ${isDynamic ? 'Saved Searches' : 'Lists'} found`
              : `This folder does not have any ${isDynamic ? 'Saved Searches' : 'Lists'} in it yet`
          }
        />
      </div>
      <CommonDeleteModal
        isOpen={isDeleteFolderModalOpen}
        onClose={closeDeleteFolderModal}
        handleDelete={confirmDeleteFolder}
        title="Delete Folder?"
        body={`This folder will be deleted, but the ${
          isDynamic ? 'saved searches' : 'lists'
        } inside it will not be affected. You can still access them in ${
          isDynamic ? 'Saved Searches' : 'Lists'
        } section.`}
        buttonLabel="Delete Folder"
      />
      <CommonDeleteModal
        isOpen={isRemoveListsModalOpen}
        onClose={closeRemoveListsModal}
        handleDelete={handleRemoveLists}
        title={`Remove ${isDynamic ? 'Saved Searches' : 'Lists'}?`}
        body={`Are you sure you want to remove these ${isDynamic ? 'saved searches' : 'lists'} from the Folder?`}
        buttonLabel={`Remove ${isDynamic ? 'Saved Searches' : 'Lists'}`}
      />
      <CommonDeleteModal
        isOpen={isBulkListsDeleteModalOpen}
        onClose={closeBulkListsDeleteModal}
        handleDelete={handleBulkListDelete}
        title="Confirm Delete"
        body={`Are you sure you want to delete these ${isDynamic ? 'saved searches' : 'lists'}?`}
        buttonLabel={`Delete ${isDynamic ? 'Saved Searches' : 'Lists'}`}
      />
    </>
  );
};

FolderListDetail.propTypes = {
  id: PropTypes.string.isRequired,
  folderName: PropTypes.string.isRequired,
  folderType: PropTypes.string.isRequired,
  folderOwner: PropTypes.number,
  loading: PropTypes.bool.isRequired,
  folderLists: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  search: PropTypes.string.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  sortModel: PropTypes.array.isRequired,
  handleSortChange: PropTypes.func.isRequired,
  paginationModel: PropTypes.object.isRequired,
  setPaginationModel: PropTypes.func.isRequired,
  updateFolder: PropTypes.func.isRequired,
  removeListFromFolder: PropTypes.func.isRequired,
  deleteFolder: PropTypes.func.isRequired,
  updateFolderLists: PropTypes.func.isRequired,
};

export default FolderListDetail;
